import React, {
  useReducer,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import {
  MagicSvgContext,
  MagicSvgDispatchContext,
} from './MagicSvgContext';
import {
  magicSvgReducer,
  PAGES,
  magicSvgActions,
  STATUS,
} from './magicSvgReducer';
import { fetchUserToken, generateImage, fetchRecentImages } from '../../api/magic-svg';
import useUser from '../UserContext/useUser';
import { fetchState } from '../UserContext';

const EMPTY_IMAGE = {};
const SVG_DEEP_LINK_LOCAL_STORAGE_KEY = 'magicSvgImage';

export default function MagicSvgProvider({ children, initialImage = {} }) {
  const [state, dispatch] = useReducer(magicSvgReducer, {
    userToken: {},
    image: initialImage,
    recentImages: [],
    generationStatus: initialImage.id ? STATUS.SUCCESS : '',
    showBanner: false,
    userAction: '',
  });
  const router = useRouter();
  const { user, userFetchState } = useUser();
  const isLoggedIn = userFetchState === fetchState.COMPLETE && Object.entries(user).length > 0;

  useEffect(() => {
    if (userFetchState === fetchState.COMPLETE) {
      if (!isLoggedIn && Object.keys(initialImage).length > 0) {
        localStorage.setItem(SVG_DEEP_LINK_LOCAL_STORAGE_KEY, JSON.stringify(initialImage));
      }

      if (isLoggedIn && Object.keys(initialImage).length === 0) {
        const storedImage = JSON.parse(localStorage.getItem(SVG_DEEP_LINK_LOCAL_STORAGE_KEY));

        if (storedImage) {
          localStorage.removeItem(SVG_DEEP_LINK_LOCAL_STORAGE_KEY);
          dispatch({
            type: magicSvgActions.SET_IMAGE,
            image: storedImage,
          });

          dispatch({
            type: magicSvgActions.SET_GENERATION_STATUS,
            status: STATUS.SUCCESS,
          });
          router.push({
            query: {
              ...router.query,
              result: storedImage.id,
              page: PAGES.RESULT,
              shallow: true,
            }
          });
        }
      }
    }
  }, [userFetchState, isLoggedIn, initialImage, router]);

  useEffect(() => {
    async function refreshToken() {
      try {
        const { userTokenomics } = await fetchUserToken();
        dispatch({
          type: magicSvgActions.SET_USER_TOKEN,
          userToken: userTokenomics,
        });
      } catch (e) {
        console.error(e);
      }
    }
    if (isLoggedIn) {
      refreshToken();
    }
  }, [isLoggedIn, state.image]);

  useEffect(() => {
    async function getRecentImages() {
      try {
        const recentImages = await fetchRecentImages();

        dispatch({
          type: magicSvgActions.SET_RECENT_IMAGES,
          recentImages,
        });
      } catch (e) {
        console.error(e);
      }
    }

    if (isLoggedIn) {
      getRecentImages();
    }
  }, [isLoggedIn]);

  const handleGenerate = useCallback(async (prompt, artStyle, additionalFields) => {
    dispatch({
      type: magicSvgActions.SET_GENERATION_STATUS,
      status: STATUS.LOADING,
    });
    const { result, ...queryWithoutResult } = router.query;
    router.push({
      query: {
        ...queryWithoutResult,
        page: PAGES.RESULT,
        shallow: true,
      }
    });


    try {
      const {
        updatedTokens,
        ...image
      } = await generateImage({ prompt, artStyle, additionalFields });
      dispatch({
        type: magicSvgActions.SET_IMAGE,
        image,
      });

      dispatch({
        type: magicSvgActions.SET_USER_TOKEN,
        userToken: updatedTokens,
      });

      dispatch({
        type: magicSvgActions.SET_GENERATION_STATUS,
        status: STATUS.SUCCESS,
      });
    } catch (e) {
      if (e.message.includes('402')) {
        router.push({
          query: {
            ...router.query,
            page: PAGES.GENERATE,
            shallow: true,
          }
        });
        dispatch({
          type: magicSvgActions.REGISTER_INSUFFICIENT_TOKEN_ATTEMPT,
        });
      }
      console.error(e);
      dispatch({
        type: magicSvgActions.SET_GENERATION_STATUS,
        status: STATUS.FAILED,
      });
    }
  }, [router]);

  return (
    <MagicSvgContext.Provider
      value={useMemo(() => ({
        ...state,
        image: state.generationStatus === STATUS.SUCCESS ? state.image : EMPTY_IMAGE,
      }), [state])}>
      <MagicSvgDispatchContext.Provider
        value={useMemo(() => ({
          onGenerate: handleGenerate,
          dispatch,
        }), [handleGenerate])}>
        {children}
      </MagicSvgDispatchContext.Provider>
    </MagicSvgContext.Provider>
  );
};

export const MagicSvgImageShape = {
  id: PropTypes.string,
  prompt: PropTypes.string,
  artStyle: PropTypes.string,
  additionalFields: PropTypes.object,
  imageUrl: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  createdAt: PropTypes.string,
};

MagicSvgProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialImage: PropTypes.shape(MagicSvgImageShape).isRequired,
};
